import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ValidationErrors, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { truncate } from 'lodash-es';
import {
  catchError,
  delay,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  HasPermissionDirective,
  isHttpErrorResponse,
  UserService,
} from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { EmptyStatePlaceholderComponent } from '../../components/placeholders/empty-state-placeholder/empty-state-placeholder.component';

import { TreeNode } from '../../models/common/tree-node.model';
import { User } from '../../models/user.model';
import { RoundPipe } from '../../pipes/round.pipe';
import { buildTreeFrom } from '../../utils/tree-node';
import { TreeViewComponent } from './components/tree-view/tree-view.component';
import { UnitCardComponent } from './components/unit-card/unit-card.component';
import { UnitSearchComponent } from './components/unit-search/unit-search.component';
import { UnitService } from './services/unit.service';
import {
  UnitResponseModel,
} from './units.model';

@Component({
  selector: 'app-units',
  imports: [
    TreeViewComponent,
    UnitCardComponent,
    UnitSearchComponent,
    MatIcon,
    HeaderComponent,
    MatIconButton,
    MatTooltip,
    MatButton,
    RoundPipe,
    EmptyStatePlaceholderComponent,
    HasPermissionDirective,
  ],
  templateUrl: './units.component.html',
  styleUrl: './units.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block h-full bg-gray-100',
  },
})
export class UnitsComponent implements OnInit {
  private readonly _route = inject(Router);
  private readonly _location = inject(Location);
  private readonly _dialog = inject(DialogService);
  private readonly _units = inject(UnitService);
  private readonly _snackbar = inject(MatSnackBar);
  protected readonly user = inject<UserService<User>>(UserService);
  protected readonly units = signal<UnitResponseModel[]>([]);
  protected readonly treeNodes = computed<TreeNode[]>(() =>
    this.buildTree(this.units())
  );
  protected readonly showPlaceholder = signal(false);

  protected readonly isRoot = computed(() => {
    const firstNode = this.treeNodes().at(0);
    const userData = this.user.data();

    return firstNode ? firstNode.id === userData?.rootUnitId : false;
  });

  protected readonly currentUnit = this._units.currentUnit;
  protected readonly searchPanelOpen = signal(false);
  protected readonly matchingUnitIds = signal<Set<string> | null>(null);

  protected readonly canReadDevices = computed(
    () => this.user.data()?.permissions?.includes('device:read') ?? false
  );
  protected readonly canReadClients = computed(
    () => this.user.data()?.permissions?.includes('client:read') ?? false
  );


  private readonly _treeView = viewChild(TreeViewComponent);
  private readonly _unitSearch = viewChild(UnitSearchComponent);

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'f' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();

      if (!this.searchPanelOpen()) {
        this.searchPanelOpen.set(true);
      } else {
        this._unitSearch()?.focusInput();
      }
      return;
    }

    // Forward printable characters to the search input
    if (
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      !(event.target instanceof HTMLInputElement) &&
      !(event.target instanceof HTMLTextAreaElement) &&
      !(event.target instanceof HTMLSelectElement)
    ) {
      event.preventDefault();

      if (!this.searchPanelOpen()) {
        this.searchPanelOpen.set(true);

        // Retry until Angular has rendered the search component
        const key = event.key;
        const applyKey = () => {
          const search = this._unitSearch();
          if (search) {
            search.appendToSearch(key);
          } else {
            setTimeout(applyKey);
          }
        };
        setTimeout(applyKey);
      } else {
        this._unitSearch()?.appendToSearchIfBlurred(event.key);
      }
    }
  }

  ngOnInit(): void {
    this.fetchUnits()
      .pipe(
        delay(50),
        tap(() => this._treeView()?.center())
      )
      .subscribe();
  }

  public goBack(): void {
    this._route.navigate(['..']);
  }

  public toggleSearch(): void {
    const willClose = this.searchPanelOpen();
    this.searchPanelOpen.update((open) => !open);
    if (willClose) {
      this.matchingUnitIds.set(null);
    }
  }

  public closeSearch(): void {
    this.searchPanelOpen.set(false);
    this.matchingUnitIds.set(null);
  }

  public onMatchingUnitIds(ids: Set<string> | null): void {
    this.matchingUnitIds.set(ids);
  }

  public onFocusUnit(unitId: string): void {
    this._treeView()?.panToNode(unitId);
  }

  public onNavigateToUnit(unitId: string): void {
    const unit = this.getUnitById(unitId);
    if (unit) {
      this.activateUnit(unit);
    }
  }

  public onNavigateToDevice(event: { unitId: string; category: string | null }): void {
    const subPath = event.category === 'access-point'
      ? 'devices/access-points'
      : 'devices/robots';
    this._navigateToUnitEntity(event.unitId, subPath);
  }

  public onNavigateToClient(event: { unitId: string; clientId: string }): void {
    this._navigateToUnitEntity(event.unitId, 'clients', event.clientId);
  }

  public isUnitDimmed(unitId: string): boolean {
    const ids = this.matchingUnitIds();
    return ids !== null && !ids.has(unitId);
  }

  public isUnitHighlighted(unitId: string): boolean {
    const ids = this.matchingUnitIds();
    return ids !== null && ids.has(unitId);
  }

  public activateUnit(unit: UnitResponseModel) {
    if (unit.deleteAt) {
      return;
    }

    this._units
      .activate(unit._id)
      .pipe(
        catchError(() => {
          this._dialog.alert({
            title: 'Activeren mislukt',
            message:
              'Er is iets misgegaan bij het activeren van de unit. Probeer het later nog eens',
            confirmText: 'OK',
          });

          return EMPTY;
        }),
        switchMap(() => this.user.fetch())
      )
      .subscribe({
        next: () => {
          this._route.navigate(['/clients']);
        },
      });
  }

  public restoreUnit(unit: UnitResponseModel): void {
    this._units
      .restore(unit._id)
      .pipe(
        tap(() => {
          this._snackbar.open(
            `Unit ${truncate(unit.name)} is succesvol hersteld`,
            'OK'
          );
        }),
        catchError(() => {
          this._dialog.alert({
            title: 'Herstellen mislukt',
            message:
              'Er is iets misgegaan bij het herstellen van de unit. Probeer het later nog eens',
            confirmText: 'OK',
          });

          return EMPTY;
        }),
        switchMap(() => this.fetchUnits())
      )
      .subscribe();
  }

  public getUnitById(unitId: string): UnitResponseModel {
    return this.units().find((unit) => unit._id === unitId)!;
  }

  public mapToTreeNode(unit: UnitResponseModel): TreeNode {
    return {
      id: unit._id,
      name: unit.name,
      parentId: unit.prevUnitId,
      children: [],
    };
  }

  public buildTree(units: UnitResponseModel[]) {
    const t = buildTreeFrom(units, this.mapToTreeNode);
    return t;
  }

  public fetchUnits(): Observable<void> {
    return this._units.getAll().pipe(
      tap({
        next: (response) => {
          this.showPlaceholder.set(response.length === 1);
        },
      }),
      map((response) => this.units.set(response)),
      map(() => undefined)
    );
  }

  public addUnit(unitId: string, direction: 'top' | 'bottom'): void {
    this._dialog.prompt({
      title: 'Vul de unit naam in',
      message: '',
      confirmText: 'Aanmaken',
      cancelText: 'Annuleren',
      input: {
        mode: 'input',
        placeholder: 'Naam unit',
        validators: {
          required: {
            validator: Validators.required,
            errorMessage: 'Vul een unit naam in',
          },
          maxlength: {
            validator: Validators.maxLength(100),
            errorMessage: (args) => {
              const errors = args as ValidationErrors;

              return `Maximaal ${errors['requiredLength']} karakters`;
            },
          },
        },
      },
      onConfirm: (name) =>
        of(name).pipe(
          filter((name): name is string => !!name),
          switchMap((name) =>
            this._units.create({
              unitId,
              direction,
              name,
            })
          ),
          catchError((e) => {
            const dialog = {
              title: 'Fout melding',
              message:
                'Er gaat iets fout met het aanmaken van de unit. Probeer het later nog eens.',
              confirmText: 'Begrepen',
            };

            if (isHttpErrorResponse(e)) {
              switch (e.error.detail.errorCode) {
                case 'UNIT_NAME_ALREADY_IN_USE':
                  dialog.title = 'Unit naam is in ingebruik';
                  dialog.message =
                    'De door jouw opgegeven unit naam is al in gebruikt. Probeer een andere naam op te gegeven.';

                  break;
              }
            }

            this._dialog.alert(dialog);

            return of(undefined);
          }),
          switchMap(() => this.fetchUnits())
        ),
    });
  }

  public editUnit(unit: UnitResponseModel): void {
    this._dialog.prompt({
      title: 'Unit bewerken',
      message: '',
      confirmText: 'Bewerken',
      cancelText: 'Annuleren',
      input: {
        mode: 'input',
        defaultValue: unit.name,
        placeholder: 'Naam unit',
        validators: {
          required: {
            validator: Validators.required,
            errorMessage: 'Vul een unit naam in',
          },
          maxlength: {
            validator: Validators.maxLength(100),
            errorMessage: (args) => {
              const errors = args as ValidationErrors;

              return `Maximaal ${errors['requiredLength']} karakters`;
            },
          },
        },
      },
      onConfirm: (name) =>
        of(name).pipe(
          filter((name): name is string => !!name),
          switchMap((name) =>
            this._units
              .update(unit._id, {
                name,
              })
              .pipe(
                tap({
                  next: () =>
                    this.units.update((units) =>
                      units.map((u) =>
                        u._id === unit._id
                          ? {
                              ...u,
                              name,
                            }
                          : u
                      )
                    ),
                })
              )
          ),
          catchError((e) => {
            const dialog = {
              title: 'Fout melding',
              message:
                'Er gaat iets fout bij het bewerken van de unit. Probeer het later nog eens.',
              confirmText: 'Begrepen',
            };

            if (isHttpErrorResponse(e)) {
              switch (e.error.detail.errorCode) {
                case 'UNIT_NAME_ALREADY_IN_USE':
                  dialog.title = 'Unit naam is in ingebruik';
                  dialog.message =
                    'De door jouw opgegeven unit naam is al in gebruikt. Probeer een andere naam op te gegeven.';

                  break;
              }
            }

            this._dialog.alert(dialog);

            return of(undefined);
          })
        ),
    });
  }

  // public deleteUnit(unit: UnitResponseModel): void {
  //   this._dialog.confirm({
  //     title: 'Unit verwijderen',
  //     message: `Weet je zeker dat je de unit <strong>${unit.name}</strong> wilt verwijderen?`,
  //     confirmText: 'Verwijderen',
  //     cancelText: 'Annuleren',
  //     onConfirm: () =>
  //       this._units.delete(unit._id).pipe(
  //         catchError(() => {
  //           this._dialog.alert({
  //             title: 'Fout melding',
  //             message:
  //               'Er gaat iets fout met het aanmaken van de unit. Probeer het later nog eens.',
  //             confirmText: 'Begrepen',
  //           });

  //           return of(undefined);
  //         }),
  //         switchMap(() => this.fetchUnits())
  //       ),
  //   });
  // }

  private _navigateToUnitEntity(
    unitId: string,
    entityPath: string,
    entityId?: string
  ): void {
    const unit = this.getUnitById(unitId);
    if (!unit) {
      return;
    }

    this._units
      .activate(unitId)
      .pipe(
        catchError(() => {
          this._snackbar.open(
            'Kon unit niet activeren',
            'OK'
          );
          return EMPTY;
        }),
        switchMap(() => this.user.fetch())
      )
      .subscribe({
        next: () => {
          const path = entityId
            ? `${entityPath}/${entityId}`
            : entityPath;
          window.location.href = this._location.prepareExternalUrl(path);
        },
      });
  }
}
